import React, { FC, useEffect, useState } from 'react';
import { Box, Typography, IconButton, Fade, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import { MessagePayload } from 'firebase/messaging';
import { isExternalUrl, processNotificationUrl } from '@/shared/utils/notification-url-handler';

export interface PushNotificationBannerProps {
  notification: MessagePayload | null;
  onClose: () => void;
  autoHideDuration?: number;
}

const PushNotificationBanner: FC<PushNotificationBannerProps> = ({
  notification,
  onClose,
  autoHideDuration = 15000
}) => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (notification) {
      setVisible(true);

      // Auto hide after duration
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [notification, autoHideDuration]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300); // Wait for fade out animation
  };

  const handleClick = () => {
    // Handle navigation to deeplink
    const rawUrl = notification?.data?.url || notification?.fcmOptions?.link;

    if (rawUrl) {
      // Process the URL to remove custom scheme if present
      const processedUrl = processNotificationUrl(rawUrl);

      // Check if it's an external URL
      if (isExternalUrl(processedUrl)) {
        window.open(processedUrl, '_blank');
      } else {
        // Internal route navigation
        navigate(processedUrl);
      }
    }

    handleClose();
  };

  if (!notification) {
    return null;
  }

  const title = notification.notification?.title || notification.data?.title || 'New Notification';
  const body = notification.notification?.body || notification.data?.body || '';
  const icon = notification.notification?.icon || notification.data?.icon;

  return (
    <Fade in={visible}>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          maxWidth: 400,
          width: '100%',
          zIndex: 9999,
          cursor: 'pointer',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
          }
        }}
        onClick={handleClick}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 1
          }}
        >
          {/* Notification Icon */}
          <Box
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {icon ? (
              <img
                src={icon}
                alt="notification"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%'
                }}
              />
            ) : (
              <NotificationsIcon sx={{ color: 'white', fontSize: 24 }} />
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                color: 'white',
                wordBreak: 'break-word'
              }}
            >
              {title}
            </Typography>
            {body && (
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {body}
              </Typography>
            )}
          </Box>

          {/* Close Button */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Fade>
  );
};

export default PushNotificationBanner;
